import {
  ChangeEvent,
  DragEvent as ReactDragEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createScan,
  fetchVideoHealth,
  listPresets,
  submitBatch,
  type ImageSource,
} from '../api';
import type { VideoGenPreset } from '../types';

interface GenerateScanModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

type SubmitMode = 'single' | 'batch';

/** Header-ish values a CSV's first column may hold — never a real scan ID. */
const CSV_HEADER_LABELS = ['scan_id', 'scanid', 'id', 'scan', 'inspection_id', 'inspectionid'];

/**
 * Pull scan IDs out of a CSV/TXT file's text: first column of each
 * non-empty line, minus anything that looks like a header. Matches the
 * parsing the other apps' batch uploads do.
 */
function parseScanIdsFromCsv(text: string): string[] {
  const ids: string[] = [];
  for (const line of text.split(/[\r\n]+/)) {
    if (!line.trim()) continue;
    const firstColumn = line.split(',')[0]?.trim();
    if (!firstColumn) continue;
    if (CSV_HEADER_LABELS.includes(firstColumn.toLowerCase())) continue;
    if (!/^[a-zA-Z0-9._-]+$/.test(firstColumn) || firstColumn.length <= 3) continue;
    ids.push(firstColumn);
  }
  return ids;
}

export function GenerateScanModal({ open, onClose, onSubmitted }: GenerateScanModalProps) {
  const [mode, setMode] = useState<SubmitMode>('single');
  const [scanId, setScanId] = useState('');
  const [batchFileName, setBatchFileName] = useState<string | null>(null);
  const [batchScanIds, setBatchScanIds] = useState<string[]>([]);
  const [imageSource, setImageSource] = useState<ImageSource>('uvpaint');
  const [presets, setPresets] = useState<VideoGenPreset[]>([]);
  const [presetId, setPresetId] = useState<string>('');
  // `null` until the server's default arrives; submitting then omits the field
  // so the run inherits it rather than being forced off by a failed probe.
  const [damageDebug, setDamageDebug] = useState<boolean | null>(null);
  // Matches the server's `create_hotspots` default.
  const [createHotspots, setCreateHotspots] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A tick that lands before the health probe must not be overwritten by it.
  const damageDebugTouched = useRef(false);

  useEffect(() => {
    if (open) {
      setMode('single');
      setScanId('');
      setBatchFileName(null);
      setBatchScanIds([]);
      setImageSource('uvpaint');
      setDamageDebug(null);
      setCreateHotspots(true);
      damageDebugTouched.current = false;
      setError(null);
      setSubmitting(false);
      setDragging(false);
      requestAnimationFrame(() => inputRef.current?.focus());
      listPresets()
        .then((list) => {
          setPresets(list.presets);
          setPresetId(list.default_id || (list.presets[0]?.id ?? ''));
        })
        .catch(() => {
          // Non-fatal: without presets the scan just uses the server default.
          setPresets([]);
          setPresetId('');
        });
      fetchVideoHealth()
        .then((health) => {
          if (!damageDebugTouched.current) setDamageDebug(health.damage_debug ?? false);
        })
        .catch(() => {
          // Leave it unknown so the request inherits the server's setting.
        });
    }
  }, [open]);

  if (!open) return null;

  const readCsvFile = (file: File) => {
    setError(null);
    setBatchFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const ids = text ? parseScanIdsFromCsv(text) : [];
      setBatchScanIds(ids);
      if (ids.length === 0) setError('No scan IDs found in that file');
    };
    reader.onerror = () => setError('Could not read that file');
    reader.readAsText(file);
    // Allow re-picking the same file after a clear.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) readCsvFile(file);
  };

  const handleDrop = (e: ReactDragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (submitting) return;
    const file = e.dataTransfer.files?.[0];
    if (file) readCsvFile(file);
  };

  // Without this, a drop that lands on the overlay instead of the zone is
  // handled by the browser: it navigates the tab to the dropped file, which
  // reads as "the page refreshed and nothing happened" and loses the form.
  const swallowDrag = (e: ReactDragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const clearBatch = () => {
    setBatchFileName(null);
    setBatchScanIds([]);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // One options object for both paths — a batch is exactly these settings
    // applied to every row of the CSV.
    const options = {
      imageSource,
      presetId: presetId || undefined,
      // Only meaningful alongside hotspots, so don't send a stale tick that
      // the greyed-out checkbox no longer shows.
      damageDebug: createHotspots ? damageDebug ?? undefined : undefined,
      createHotspots,
    };

    if (mode === 'batch') {
      if (batchScanIds.length === 0 || !batchFileName) {
        setError('Upload a CSV with at least one scan ID');
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        await submitBatch(batchFileName, batchScanIds, options);
      } catch (ex) {
        setError(ex instanceof Error ? ex.message : 'Failed to submit batch');
        setSubmitting(false);
        return;
      }
    } else {
      const trimmed = scanId.trim();
      if (!trimmed) {
        setError('Scan ID is required');
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        await createScan(trimmed, options);
      } catch (ex) {
        setError(ex instanceof Error ? ex.message : 'Failed to submit');
        setSubmitting(false);
        return;
      }
    }

    onSubmitted();
    onClose();
    // Send the user to the video Live page so they immediately see their
    // freshly-submitted work.
    navigate('/video/live');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-900/30 backdrop-blur-sm px-4"
      onClick={onClose}
      // The overlay covers the viewport while the modal is open, so catching
      // drags here stops a near-miss from navigating the tab to the file.
      onDragOver={swallowDrag}
      onDrop={(e) => {
        swallowDrag(e);
        setDragging(false);
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-card-hover border border-primary-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-primary-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-900">
            {mode === 'batch' ? 'Generate Batch' : 'Generate Scan'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-primary-400 hover:text-primary-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <span className="block text-sm font-medium text-primary-800 mb-1">Submit</span>
            <div className="grid grid-cols-2 gap-2">
              {([
                { id: 'single', label: 'Single scan' },
                { id: 'batch', label: 'CSV batch' },
              ] as const).map((opt) => {
                const active = mode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setMode(opt.id);
                      setError(null);
                    }}
                    disabled={submitting}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 ${
                      active
                        ? 'border-primary-500 bg-primary-50 text-primary-900 ring-2 ring-primary-200'
                        : 'border-primary-200 text-primary-700 hover:bg-primary-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-primary-800 mb-1">Image source</span>
            <div className="grid grid-cols-2 gap-2">
              {([
                { id: 'uvpaint', label: 'UV-Paint' },
                { id: 'lightning', label: 'Lightning output' },
              ] as const).map((opt) => {
                const active = imageSource === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setImageSource(opt.id)}
                    disabled={submitting}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 ${
                      active
                        ? 'border-primary-500 bg-primary-50 text-primary-900 ring-2 ring-primary-200'
                        : 'border-primary-200 text-primary-700 hover:bg-primary-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {presets.length > 0 && (
            <label className="block">
              <span className="block text-sm font-medium text-primary-800 mb-1">Settings preset</span>
              <select
                value={presetId}
                onChange={(e) => setPresetId(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 rounded-lg border border-primary-200 bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm disabled:opacity-60"
              >
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {imageSource === 'uvpaint' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 leading-snug">
              <span className="font-semibold">Heads up:</span> only submit scans whose merch images
              are <span className="font-semibold">published</span> in UVCamp. The scan must have all four
              of <code className="font-mono text-amber-900">center_front</code>,{' '}
              <code className="font-mono text-amber-900">center_rear</code>,{' '}
              <code className="font-mono text-amber-900">center_left</code> and{' '}
              <code className="font-mono text-amber-900">center_right</code>.
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 leading-snug">
              <span className="font-semibold">Heads up:</span> this uses the scan's{' '}
              <span className="font-semibold">360 Lightning</span> output. The scan must have a{' '}
              <span className="font-semibold">completed</span> Lightning run with rendered{' '}
              <code className="font-mono text-amber-900">front</code>,{' '}
              <code className="font-mono text-amber-900">rear</code>,{' '}
              <code className="font-mono text-amber-900">left</code> and{' '}
              <code className="font-mono text-amber-900">right</code> angles. Scans with no
              Lightning run fall back to their <span className="font-semibold">Mobile</span>{' '}
              render, which produces the same poses — those runs never get hotspots.
            </div>
          )}

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={createHotspots}
              onChange={(e) => setCreateHotspots(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 w-4 h-4 rounded border-primary-300 text-primary-600 focus:ring-2 focus:ring-primary-200 disabled:opacity-60"
            />
            <span>
              <span className="block text-sm font-medium text-primary-800">Compute hotspots</span>
              <span className="block text-[12px] text-primary-500 leading-snug">
                Blue feature points and red damage markers for the 360 viewer. Untick to generate
                the video only.
              </span>
            </span>
          </label>

          {/* Nested under hotspots: the annotated images are a by-product of
              damage localization, which only runs when hotspots do. */}
          <label
            className={`flex items-start gap-2.5 ${createHotspots ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          >
            <input
              type="checkbox"
              checked={createHotspots && (damageDebug ?? false)}
              onChange={(e) => {
                damageDebugTouched.current = true;
                setDamageDebug(e.target.checked);
              }}
              disabled={submitting || !createHotspots}
              className="mt-0.5 w-4 h-4 rounded border-primary-300 text-primary-600 focus:ring-2 focus:ring-primary-200 disabled:opacity-60"
            />
            <span>
              <span className="block text-sm font-medium text-primary-800">
                Damage debug images
              </span>
              <span className="block text-[12px] text-primary-500 leading-snug">
                Keeps the annotated localization images (original frame, camera stitch, keyframe)
                for the Debug panel's Damages tab.
              </span>
            </span>
          </label>

          {mode === 'single' ? (
            <label className="block">
              <span className="block text-sm font-medium text-primary-800 mb-1">Scan ID</span>
              <input
                ref={inputRef}
                value={scanId}
                onChange={(e) => setScanId(e.target.value)}
                placeholder="e.g. cc47dfec-04ba-4cff-9bcb-196c1b1af4b9"
                disabled={submitting}
                className="w-full px-3 py-2 rounded-lg border border-primary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm font-mono disabled:opacity-60"
              />
            </label>
          ) : (
            <div>
              <span className="block text-sm font-medium text-primary-800 mb-1">Scan ID list</span>
              <label
                onDragEnter={(e) => {
                  swallowDrag(e);
                  if (!submitting) setDragging(true);
                }}
                onDragOver={(e) => {
                  swallowDrag(e);
                  if (!submitting) setDragging(true);
                }}
                onDragLeave={(e) => {
                  swallowDrag(e);
                  setDragging(false);
                }}
                onDrop={handleDrop}
                className={`block rounded-lg border-2 border-dashed px-3 py-6 text-center transition-colors ${
                  submitting
                    ? 'border-primary-200 opacity-60'
                    : dragging
                      ? 'border-primary-500 bg-primary-50 cursor-pointer'
                      : 'border-primary-200 cursor-pointer hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  disabled={submitting}
                  className="hidden"
                />
                <span className="block text-sm text-primary-600">
                  Drop a CSV here or <span className="font-medium text-primary-800">browse</span>
                </span>
                <span className="block text-[12px] text-primary-400 mt-1">
                  One scan ID per line (first column). A header row is ignored.
                </span>
              </label>

              {batchScanIds.length > 0 && (
                <div className="mt-2 rounded-lg border border-primary-100 bg-primary-50/60 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-primary-800">
                      <span className="font-semibold">{batchScanIds.length}</span> scan
                      {batchScanIds.length === 1 ? '' : 's'} from{' '}
                      <span className="font-mono text-[12px]">{batchFileName}</span>
                    </span>
                    <button
                      type="button"
                      onClick={clearBatch}
                      disabled={submitting}
                      className="text-[12px] text-primary-500 hover:text-primary-800 transition-colors disabled:opacity-60"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="mt-1.5 space-y-0.5 max-h-24 overflow-y-auto">
                    {batchScanIds.slice(0, 10).map((id, idx) => (
                      <div key={`${id}-${idx}`} className="font-mono text-[11px] text-primary-600">
                        {id}
                      </div>
                    ))}
                    {batchScanIds.length > 10 && (
                      <div className="text-[11px] text-primary-400">
                        … and {batchScanIds.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (mode === 'batch' && batchScanIds.length === 0)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-gradient shadow-card hover:shadow-card-hover hover:opacity-95 transition-all disabled:opacity-60"
            >
              {submitting
                ? 'Submitting…'
                : mode === 'batch'
                  ? `Submit Batch${batchScanIds.length > 0 ? ` (${batchScanIds.length})` : ''}`
                  : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
